import React, { useState, useRef, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Code,
  Plus,
  Eye,
  Edit,
  Trash2,
  Search,
  Calendar,
  ChevronDown,
  ChevronUp,
  Filter,
  Download,
  Upload,
  FileText,
  FileSpreadsheet,
  FileOutput,
  List,
  Grid,
  RefreshCcw,
  CheckCircle,
  AlertTriangle,
  Layers,
  ListChecks,
  Package,
  LayoutList,
  Users
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTimeTracking } from '../../core/contexts/TimeTrackingContext';
import { CustomSelect, CustomDatePicker } from '../../shared/components/custom';
import QawaemErpCrudModal from '../../products/saudi/components/QawaemErpCrudModal';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import useModuleConfigTheme from '../../shared/hooks/useModuleConfigTheme';
import {
  usePointageModalBodyLock,
  getPointageModalFormClasses,
  buildPointageBulkDeleteFilterAction,
  POINTAGE_ACTIONS_TH_CLASS,
  buildPointageColumnVisibilityState,
  buildPointageQawaemColumnDefs,
  isPointageColumnVisible,
  createPointageHandleToggleColumn,
  pointageTableColSpan,
  PointageCrudNotificationToast,
  useAppNotificationToast,
  notifyBulkDeleted,
  notifyCreated,
  notifyDeleted,
  notifyError,
  notifyUpdated,
  sortRecordsNewestFirst,
} from './pointageListPageUtils';
import PointageFormModalShell, { POINTAGE_FORM_GRID_CLASS } from './components/PointageFormModalShell';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import { getTimeTrackingCodeLabel } from '../../shared/utils/timeTrackingI18n';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  useQawaemFilteredRowSelection,
} from '../../products/saudi/components/QawaemTableSelection';

const NS = 'modules.pointage.pages.timeTrackingCodesPage';

const TimeTrackingCodesPage = () => {
  const { t } = useTranslation();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const p = (key, opts) => t(`${NS}.${key}`, opts);
  const {
    timeTrackingCodes,
    addTimeTrackingCode,
    updateTimeTrackingCode,
    deleteTimeTrackingCode
  } = useTimeTracking();
  
  // Helper function to determine text color based on background brightness
  const getContrastTextColor = (hexColor) => {
    if (!hexColor) return '#ffffff';
    
    // Remove # if present
    const hex = hexColor.replace('#', '');
    
    // Convert to RGB
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Calculate luminance (perceived brightness)
    // Formula: https://www.w3.org/TR/WCAG20/#relativeluminancedef
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Return dark gray for light backgrounds, white for dark backgrounds
    return luminance > 0.6 ? '#374151' : '#ffffff'; // Gray-700 instead of black
  };
  
  // ===== STATE MANAGEMENT =====
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'cards'
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  
  // Date range filter
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  
  // Dropdown states
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  
  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const cfgTheme = useModuleConfigTheme();
  const modalForm = getPointageModalFormClasses(cfgTheme.light);
  usePointageModalBodyLock(showAddModal);
  const [editingCode, setEditingCode] = useState(null);
  
  // Filters
  const [quickFilter, setQuickFilter] = useState('all');
  
  // Form data
  const [newCode, setNewCode] = useState({
    code: '',
    label: '',
    description: '',
    color: 'bg-green-500',
    type: 'employee'
  });
  
  // Refs
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const dateRangeRef = useRef(null);
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();

  // ===== EFFECTS =====
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
      if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) {
        setIsDateRangeOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ===== HELPER FUNCTIONS =====
  // Employee attendance codes only
  const employeeCodes = timeTrackingCodes.filter((code) => {
    if (code.type) {
      return code.type === 'employee' || code.type === 'employé';
    }
    return !code.code.startsWith('V-') && !code.code.startsWith('E-');
  });

  const currentCodes = employeeCodes;

  const codeCategories = [
    {
      id: 'presence',
      label: p('category_presence'),
      count: currentCodes.filter((c) => c.code.startsWith('P') || c.code.startsWith('2P')).length,
    },
    {
      id: 'absence',
      label: p('category_absence'),
      count: currentCodes.filter((c) => c.code.startsWith('AN') || c.code.startsWith('AJ') || c.code.startsWith('AP')).length,
    },
    {
      id: 'conge',
      label: p('category_leave'),
      count: currentCodes.filter((c) => c.code.startsWith('C') || c.code.startsWith('SS')).length,
    },
  ];

  const baseFilteredCodes = useMemo(
    () => currentCodes.filter((code) => {
      const matchesSearch =
        code.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        code.label.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    }),
    [currentCodes, searchTerm],
  );

  const columnFilterRules = useMemo(
    () => [
      textColumnRule('code', (c) => c.code || ''),
      textColumnRule('label', (c) => getTimeTrackingCodeLabel(c.code, c.label, t)),
      textColumnRule('description', (c) => c.description || ''),
      textColumnRule('color', (c) => c.color || c.bgColor || ''),
    ],
    [t],
  );

  const filteredCodes = useMemo(
    () => sortRecordsNewestFirst(applyColumnFilters(baseFilteredCodes, colFilters, columnFilterRules)),
    [baseFilteredCodes, colFilters, columnFilterRules],
  );

  const {
    selectedRecords,
    setSelectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord,
  } = useQawaemFilteredRowSelection(filteredCodes, (code) => code.id || code.code);

  const handleBulkDelete = async () => {
    if (selectedRecords.length === 0) return;
    if (!window.confirm(p('delete_confirm', { code: selectedRecords.length, label: '' }))) return;
    try {
      const deleteCount = selectedRecords.length;
      selectedRecords.forEach((id) => {
        const code = currentCodes.find((c) => (c.id || c.code) === id);
        if (code) deleteTimeTrackingCode(code.id);
      });
      notifyBulkDeleted(showNotify, deleteCount, p('title'));
      setSelectedRecords([]);
    } catch (error) {
      console.error('Error bulk deleting codes:', error);
      notifyError(showNotify, p('delete_error'));
    }
  };

  // Couleurs disponibles
  const availableColors = [
    { value: 'bg-green-500', label: p('color_green') },
    { value: 'bg-blue-500', label: p('color_blue') },
    { value: 'bg-red-500', label: p('color_red') },
    { value: 'bg-yellow-500', label: p('color_yellow') },
    { value: 'bg-purple-500', label: p('color_purple') },
    { value: 'bg-pink-500', label: p('color_pink') },
    { value: 'bg-indigo-500', label: p('color_indigo') },
    { value: 'bg-gray-500', label: p('color_gray') },
  ];

  const handleAddCode = () => {
    setEditingCode(null);
    setNewCode({
      code: '',
      label: '',
      description: '',
      color: 'bg-green-500',
      type: 'employee'
    });
    setShowAddModal(true);
  };
  
  const handleEditCode = (code) => {
    setEditingCode(code);
    setNewCode({ 
      ...code,
      type: code.type === 'employee' || code.type === 'employé' ? code.type : 'employee'
    });
    setShowAddModal(true);
  };
  
  const handleDeleteCode = async (codeToDelete) => {
    if (!window.confirm(p('delete_confirm', { code: codeToDelete.code, label: codeToDelete.label }))) {
      return;
    }
    
    try {
      deleteTimeTrackingCode(codeToDelete.id);
      notifyDeleted(showNotify, p('title'));
    } catch (error) {
      console.error('Error deleting time tracking code:', error);
      notifyError(showNotify, p('delete_error'));
    }
  };
  
  const handleSaveCode = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!newCode.code || !newCode.label) {
      alert(p('required_fields'));
      return;
    }
    
    try {
      if (editingCode) {
        updateTimeTrackingCode(editingCode.id, {
          code: newCode.code,
          label: newCode.label,
          description: newCode.description,
          color: newCode.color,
          bgColor: newCode.color,
          type: newCode.type || 'employee'
        });
        notifyUpdated(showNotify, p('title'));
      } else {
        if (timeTrackingCodes.some(code => code.code === newCode.code)) {
          alert(p('duplicate_code'));
          return;
        }
        
        addTimeTrackingCode({
          ...newCode,
          bgColor: newCode.color,
          type: newCode.type || 'employee'
        });
        notifyCreated(showNotify, p('title'));
      }
      
      setShowAddModal(false);
    } catch (error) {
      console.error('Error saving time tracking code:', error);
      notifyError(showNotify, p('save_error')); return;
    }
  };

  // Calculate stats based on current view mode
  const stats = {
    total: currentCodes.length,
    presence: currentCodes.filter((c) => c.code.startsWith('P') || c.code.startsWith('2P')).length,
    absence: currentCodes.filter((c) => c.code.startsWith('A')).length,
    conge: currentCodes.filter(c => c.code.startsWith('C')).length,
    active: currentCodes.filter((c) => c.isActive !== false).length
  };

  const codeKpiCards = [
    { 
      title: p('kpi_total'), 
      value: stats.total, 
      icon: Code, 
      color: 'from-blue-500 to-blue-600' 
    },
    { 
      title: p('kpi_presence'), 
      value: stats.presence, 
      icon: CheckCircle, 
      color: 'from-green-500 to-green-600' 
    },
    { 
      title: p('kpi_absence'), 
      value: stats.absence, 
      icon: AlertTriangle, 
      color: 'from-red-500 to-red-600' 
    },
    { 
      title: p('kpi_leave'), 
      value: stats.conge, 
      icon: Package, 
      color: 'from-purple-500 to-purple-600' 
    },
    { 
      title: p('kpi_active'), 
      value: stats.active, 
      icon: ListChecks, 
      color: 'from-gray-500 to-gray-600' 
    }
  ];

  const POINTAGE_TABLE_COLUMNS = [
    { key: 'code', labelKey: 'table_code' },
    { key: 'label', labelKey: 'table_label' },
    { key: 'description', labelKey: 'table_description' },
    { key: 'color', labelKey: 'table_color' },
    { key: 'actions', label: t('common.actions'), required: true },
  ];
  const [columnVisibility, setColumnVisibility] = useState(() =>
    buildPointageColumnVisibilityState(POINTAGE_TABLE_COLUMNS),
  );
  const qawaemColumnDefs = useMemo(
    () => buildPointageQawaemColumnDefs(POINTAGE_TABLE_COLUMNS, p, columnVisibility),
    [p, t, columnVisibility],
  );
  const handleToggleColumn = createPointageHandleToggleColumn(setColumnVisibility, POINTAGE_TABLE_COLUMNS);
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => isPointageColumnVisible(columnVisibility, key);
  const tableColSpan = pointageTableColSpan(columnVisibility);

  // ===== RENDER =====
  return (
    <div className="space-y-6 clients-container">
      {/* Header */}
      <div className="flex justify-between items-center list-toolbar-mobile-layout">
        <div>
          <h1 className="text-3xl font-bold text-white">
            {p('title')}
            <button onClick={() => window.location.reload()} className="refresh-button ml-4" title={t('common.refresh')}>
              <RefreshCcw size={20} />
            </button>
          </h1>
          <p className="text-white/60 mt-2 flex items-center gap-2">
            {p('subtitle')}
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-500/20 text-blue-300 border border-blue-500/30">
              <Users className="h-3.5 w-3.5 mr-1.5" />
              {p('badge_employees')}
            </span>
          </p>
        </div>
        <div className="flex gap-3">
          {buildPointageBulkDeleteFilterAction({
            count: selectedRecords.length,
            onDelete: handleBulkDelete,
            label: `${t('common.delete')} (${selectedRecords.length})`,
            title: t('common.delete', { defaultValue: 'حذف' }),
          })}
          <button
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            className={`${
              showAdvancedOptions 
                ? 'bg-gradient-to-r from-purple-500 to-purple-600 border-purple-400' 
                : 'bg-white/5 border-white/20'
            } border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
            title={t('common.advanced_options')}
          >
            <Filter className="h-5 w-5" />
            <span className="hidden sm:inline">{t('common.advanced_options')}</span>
            {showAdvancedOptions ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>
          
          <button
            className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2 shadow-lg shadow-blue-500/30"
            onClick={handleAddCode}
          >
            <Plus className="h-5 w-5" />
            <span>{p('new_code')}</span>
          </button>
        </div>
      </div>

      {/* Stats Cards — même grille / halo que Facturation > Facturiers */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {codeKpiCards.slice(0, 4).map((stat, i) => (
          <motion.div key={stat.title} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative group">
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`}></div>
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 shadow-xl shadow-black/10">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">{stat.title}</p>
                  <p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p>
                </div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`}></div>
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                    <stat.icon className="h-6 w-6 text-white force-white" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Advanced Options - Collapsible */}
      <AnimatePresence>
        {showAdvancedOptions && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="space-y-4">
              {/* Main View Tabs */}
              <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20 shadow-xl shadow-black/10">
                <div className="flex space-x-1 overflow-x-auto hide-scrollbar">
                  <button className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 bg-gradient-to-r from-orange-600 to-red-600 text-white shadow-lg">
                    <Grid className="h-5 w-5" />
                    <span>{t('common.general_view')}</span>
                  </button>
                  <button className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 text-white/80 hover:bg-white/10 hover:text-white">
                    <Layers className="h-5 w-5" />
                    <span>{p('by_category')}</span>
                  </button>
                  <button className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 text-white/80 hover:bg-white/10 hover:text-white">
                    <CheckCircle className="h-5 w-5" />
                    <span>{t('common.by_type')}</span>
                  </button>
                </div>
              </div>

              {/* Distribution Overview */}
              <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 shadow-xl shadow-black/10">
                <h3 className="text-xl font-semibold text-white mb-4">{p('distribution_title')}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {codeCategories.map((category, index) => (
                    <div key={category.id} className="bg-white/5 rounded-xl p-4 border border-white/10">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="font-medium text-white">{category.label}</h4>
                        <div className={`text-sm rounded-full px-2 py-0.5 ${
                          index === 0 ? 'bg-green-500' : index === 1 ? 'bg-red-500' : 'bg-purple-500'
                        } text-white force-white`}>
                          {category.count}
                        </div>
                      </div>
                      <div className="w-full bg-white/10 h-3 rounded-full">
                        <div 
                          className={`h-3 rounded-full ${
                            index === 0 ? 'bg-green-500' : index === 1 ? 'bg-red-500' : 'bg-purple-500'
                          }`}
                          style={{ width: `${stats.total > 0 ? (category.count / stats.total * 100) : 0}%` }}
                        ></div>
                      </div>
                      <p className="mt-2 text-sm text-white/60">
                        {p('percent_of_codes', { percent: stats.total > 0 ? ((category.count / stats.total * 100).toFixed(1)) : 0 })}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Data Table with Integrated Header */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden shadow-xl shadow-black/10">

        

        

        {/* Table or Card View */}
        {viewMode === 'table' ? (
          // Table View
          <div className="overflow-x-auto table-horizontal-scroll">
            <table className="w-full clients-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader
                    allRowsSelected={allRowsSelected}
                    someRowsSelected={someRowsSelected}
                    onChange={handleSelectAll}
                    selectAllTitle={t('common.select_all', { defaultValue: 'تحديد الكل' })}
                    deselectAllTitle={t('common.deselect_all', { defaultValue: 'إلغاء تحديد الكل' })}
                  />
                  {isColVisible('code') && (
                    <TableColumnHeaderFilter columnKey="code" label={p('table_code')} value={colFilters.code ?? ''} onChange={(v) => setColFilter('code', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('label') && (
                    <TableColumnHeaderFilter columnKey="label" label={p('table_label')} value={colFilters.label ?? ''} onChange={(v) => setColFilter('label', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('description') && (
                    <TableColumnHeaderFilter columnKey="description" label={p('table_description')} value={colFilters.description ?? ''} onChange={(v) => setColFilter('description', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('color') && (
                    <TableColumnHeaderFilter columnKey="color" label={p('table_color')} value={colFilters.color ?? ''} onChange={(v) => setColFilter('color', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  <QawaemTableHeaderActionsCell
                    label={t('common.actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                    thClassName={POINTAGE_ACTIONS_TH_CLASS}
                  />
                </tr>
              </thead>
              <tbody className="bg-white/5">
                {filteredCodes.length > 0 ? (
                  filteredCodes.map((code) => (
                    <tr key={code.code} className="hover:bg-white/10 transition-colors border-b border-white/10">
                      <QawaemTableSelectRowCell
                        checked={selectedRecords.includes(code.id || code.code)}
                        onChange={() => handleSelectRecord(code.id || code.code)}
                      />
                      {isColVisible('code') && (
                        <td className="px-6 py-4">
                          <span className="font-bold text-white text-sm">
                            {code.code}
                          </span>
                        </td>
                      )}
                      {isColVisible('label') && (
                        <td className="px-6 py-4 text-white font-semibold">{getTimeTrackingCodeLabel(code.code, code.label, t)}</td>
                      )}
                      {isColVisible('description') && (
                        <td className="px-6 py-4 text-white/80">{code.description || p('empty_dash')}</td>
                      )}
                      {isColVisible('color') && (
                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-2">
                            <span 
                              className="px-4 py-2 rounded-lg text-sm font-bold shadow-md inline-block min-w-[80px] text-center border border-white/20"
                              style={{ 
                                backgroundColor: code.color || '#10b981',
                                color: getContrastTextColor(code.color || '#10b981')
                              }}
                            >
                              {code.code}
                            </span>
                            <span className="text-xs text-white/50 font-mono">
                              {code.color || code.bg_color || '#10b981'}
                            </span>
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-4 text-center align-middle">
                        <div className="flex justify-center">
                          <QawaemErpRowActions
                            onEdit={() => handleEditCode(code)}
                            onDelete={() => handleDeleteCode(code)}
                            editTitle={t('common.edit')}
                            deleteTitle={t('common.delete')}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={tableColSpan} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <Users className="h-12 w-12 text-white/30" />
                        <p className="text-white/50">
                          {searchTerm ? p('empty_employee_search') : p('empty_employee')}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          // Card View
          <div className="p-6">
            {filteredCodes.length > 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredCodes.map((code) => (
                  <motion.div
                    key={code.code}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 shadow-xl shadow-black/10 hover:bg-white/15 transition-all duration-300 h-full min-w-0"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-3 mb-3 flex-wrap">
                          <span 
                            className="px-4 py-2 rounded-lg text-sm font-bold shadow-lg inline-block min-w-[80px] text-center border border-white/30"
                            style={{ 
                              backgroundColor: code.color || '#10b981',
                              color: getContrastTextColor(code.color || '#10b981')
                            }}
                          >
                            {code.code}
                          </span>
                        </div>
                        <h3 className="text-white font-semibold text-lg mb-1 truncate">{getTimeTrackingCodeLabel(code.code, code.label, t)}</h3>
                        <p className="text-white/60 text-sm line-clamp-2">{code.description || p('no_description')}</p>
                      </div>
                      
                      <div className="flex gap-1 ml-4 shrink-0">
                        <QawaemErpRowActions
                          onEdit={() => handleEditCode(code)}
                          onDelete={() => handleDeleteCode(code)}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                          compact
                        />
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3 py-12">
                <Users className="h-16 w-16 text-white/30" />
                <p className="text-white/50">
                  {searchTerm ? p('empty_employee_search') : p('empty_employee')}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Modal pour ajouter ou modifier un code */}
      {showAddModal && (
        <QawaemErpCrudModal
          open={showAddModal}
          onClose={() => setShowAddModal(false)}
          title={editingCode ? p('modal_edit') : p('modal_add')}
          icon={Code}
          formId="time-tracking-code-form"
          showSaveButton
          saveLabel={editingCode ? p('update') : t('common.save')}
          maxWidth="800px"
          testId="time-tracking-codes-crud-modal"
        >
          <PointageFormModalShell
            isLight={cfgTheme.light}
            tabs={[]}
            activeTab=""
            onTabChange={() => {}}
            formId="time-tracking-code-form"
            onSubmit={handleSaveCode}
            ariaLabel={editingCode ? p('modal_edit') : p('modal_add')}
          >
              <div className={POINTAGE_FORM_GRID_CLASS}>
                <div>
                  <label className={modalForm.label}>{<RequiredLabelText text={p('code_field')} />}</label>
                  <input type="text" value={newCode.code} onChange={(e) => setNewCode({ ...newCode, code: e.target.value, type: 'employee' })} className="w-full bg-white/10 backdrop-blur-lg rounded-xl px-4 py-3 text-white border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all placeholder:text-white/30" placeholder={p('placeholder_employee')} disabled={!!editingCode} />
                  <p className="text-white/40 text-xs mt-1.5">{p('hint_employee')}</p>
                </div>
                <div>
                  <label className={modalForm.label}>{<RequiredLabelText text={p('label_field')} />}</label>
                  <input type="text" value={newCode.label} onChange={(e) => setNewCode({...newCode, label: e.target.value})} className="w-full bg-white/10 backdrop-blur-lg rounded-xl px-4 py-3 text-white border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all placeholder:text-white/30" placeholder={p('label_placeholder')} />
                </div>
                <div>
                  <label className={modalForm.label}>{p('color_field')}</label>
                  <CustomSelect value={newCode.color} onChange={(v) => setNewCode({ ...newCode, color: v })} options={availableColors.map((color) => ({ value: color.value, label: color.label }))} placeholder={p('color_placeholder')} />
                </div>
                <div className="md:col-span-3">
                  <label className={modalForm.label}>{p('description_optional')}</label>
                  <textarea value={newCode.description} onChange={(e) => setNewCode({...newCode, description: e.target.value})} className="w-full bg-white/10 backdrop-blur-lg rounded-xl px-4 py-3 text-white border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all placeholder:text-white/30 min-h-[100px] resize-y" placeholder={p('description_placeholder')} rows="3" />
                  <p className="text-white/40 text-xs mt-1.5">{p('description_hint')}</p>
                </div>
              </div>
          </PointageFormModalShell>
        </QawaemErpCrudModal>
      )}
      <PointageCrudNotificationToast toast={notifyToast} onDismiss={dismissNotify} />
    </div>
  );
};

export default TimeTrackingCodesPage;

